// PlayEngine Agent tab component. Allows running an agent against the current version with a custom prompt.
import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import UndoIcon from "@material-ui/icons/Undo";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import BugReportIcon from "@material-ui/icons/BugReport";
import HistoryIcon from "@material-ui/icons/History";
import RestoreIcon from "@material-ui/icons/Restore";
import { getServerUri } from "../modules/uriUtility";
import { runAgentOverSocket, fetchRunStatus, pollRunUntilSettled, acknowledgeRun } from "../modules/agentSocket";
import { isVersionEmpty, versionHasPreset } from "../modules/playEngineVersionUtils";
import { AgentPromptHeader, AgentContinueDialog } from "../modules/agentTabShared";
import { hasAgentCreditsRemaining, notifyAgentCreditsExhausted, handleAgentCreditsExhaustedError } from "../modules/agentPlanReset";

const CARD_BG = "#474747";
const FIELD_BG = "#313131";
const CONSOLE_BG = "#1a1a1a";

// Compact "time ago" label for checkpoint timestamps.
function timeAgo(dateValue) {
  if (!dateValue) return "";
  const then = new Date(dateValue).getTime();
  if (isNaN(then)) return "";
  const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (diffSec < 60) return "just now";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  return `${diffDay}d ago`;
}

// Exact local date + time for checkpoint timestamps (e.g. "Jun 22, 2026, 2:38:05 PM").
function formatExactDateTime(dateValue) {
  if (!dateValue) return "";
  const d = new Date(dateValue);
  if (isNaN(d.getTime())) return "";
  try {
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch (_err) {
    return d.toLocaleString();
  }
}

const EVENT_COLORS = {
  start: "#7e57c2",
  manifest: "#ab47bc",
  turn: "#42a5f5",
  tool_result: "#26a69a",
  tool_error: "#ef5350",
  done: "#66bb6a",
  error: "#ef5350",
};

// Tracks agent runs already applied in this browser session so revisiting a
// version does not replay a settled run from the in-memory server registry.
const consumedAgentRunByVersion = new Map();

// Agent results that finished while another version was selected — shown when
// the user returns to the run's version.
const pendingAgentResultsByVersion = new Map();

// Dedicated renderer for the run "manifest" event: shows exactly what context
// the agent received this run (system prompt, block sizes, tool list).
function ManifestEntry({ entry }) {
  const [open, setOpen] = useState(false);
  const color = EVENT_COLORS.manifest;
  const blocks = entry.blocks || [];
  const tools = entry.toolNames || [];

  return (
    <div style={{ borderLeft: `2px solid ${color}`, paddingLeft: "8px", marginBottom: "4px" }}>
      <div
        style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}
        onClick={() => setOpen((v) => !v)}
      >
        <Typography variant="caption" style={{ color, fontWeight: 700, minWidth: "80px", fontFamily: "monospace" }}>
          run context
        </Typography>
        <Typography variant="caption" style={{ color: "#888", fontFamily: "monospace" }}>
          {blocks.length} blocks · {tools.length} tools{entry.preflight ? " · preflight" : ""}
        </Typography>
        <span style={{ marginLeft: "auto", color: "#555" }}>
          {open ? <ExpandLessIcon style={{ fontSize: 14 }} /> : <ExpandMoreIcon style={{ fontSize: 14 }} />}
        </span>
      </div>

      <Collapse in={open}>
        {blocks.length > 0 && (
          <pre style={{
            fontSize: "11px", color: "#ccc", backgroundColor: "#111",
            padding: "6px 8px", borderRadius: "3px", marginTop: "4px",
            overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all",
            maxHeight: "200px", overflowY: "auto",
          }}>
            <span style={{ color: "#888", display: "block", marginBottom: "2px", fontSize: "10px" }}>PROMPT BLOCKS</span>
            {blocks.map((b) => `${b.name}  (${b.chars} chars)`).join("\n")}
          </pre>
        )}
        {tools.length > 0 && (
          <pre style={{
            fontSize: "11px", color: "#ccc", backgroundColor: "#111",
            padding: "6px 8px", borderRadius: "3px", marginTop: "4px",
            overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all",
            maxHeight: "200px", overflowY: "auto",
          }}>
            <span style={{ color: "#888", display: "block", marginBottom: "2px", fontSize: "10px" }}>TOOLS</span>
            {tools.join(", ")}
          </pre>
        )}
        {entry.systemPrompt && (
          <pre style={{
            fontSize: "11px", color: "#ccc", backgroundColor: "#111",
            padding: "6px 8px", borderRadius: "3px", marginTop: "4px",
            overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word",
            maxHeight: "400px", overflowY: "auto",
          }}>
            <span style={{ color: "#ab47bc", display: "block", marginBottom: "2px", fontSize: "10px" }}>SYSTEM PROMPT</span>
            {entry.systemPrompt}
          </pre>
        )}
      </Collapse>
    </div>
  );
}

function LogEntry({ entry }) {
  const [open, setOpen] = useState(false);
  const color = EVENT_COLORS[entry.event] || "#aaa";

  if (entry.event === "manifest") return <ManifestEntry entry={entry} />;

  const hasDetail = entry.input || entry.result || entry.text || entry.tools_called || entry.error;

  return (
    <div style={{ borderLeft: `2px solid ${color}`, paddingLeft: "8px", marginBottom: "4px" }}>
      <div
        style={{ display: "flex", alignItems: "center", gap: "6px", cursor: hasDetail ? "pointer" : "default" }}
        onClick={() => hasDetail && setOpen((v) => !v)}
      >
        <Typography variant="caption" style={{ color, fontWeight: 700, minWidth: "80px", fontFamily: "monospace" }}>
          {entry.event}
        </Typography>
        {entry.turn && (
          <Typography variant="caption" style={{ color: "#888", fontFamily: "monospace" }}>
            turn {entry.turn}
          </Typography>
        )}
        {entry.tool && (
          <Typography variant="caption" style={{ color: "#ffa726", fontFamily: "monospace" }}>
            {entry.tool}
          </Typography>
        )}
        {entry.stop_reason && (
          <Typography variant="caption" style={{ color: "#888", fontFamily: "monospace" }}>
            › {entry.stop_reason}
          </Typography>
        )}
        {entry.note && (
          <Typography variant="caption" style={{ color: "#888", fontFamily: "monospace" }}>
            ({entry.note})
          </Typography>
        )}
        {entry.error && !open && (
          <Typography variant="caption" style={{ color: "#ef5350", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "200px" }}>
            {entry.error}
          </Typography>
        )}
        {hasDetail && (
          <span style={{ marginLeft: "auto", color: "#555" }}>
            {open ? <ExpandLessIcon style={{ fontSize: 14 }} /> : <ExpandMoreIcon style={{ fontSize: 14 }} />}
          </span>
        )}
      </div>

      <Collapse in={open}>
        {entry.input && (
          <pre style={{
            fontSize: "11px", color: "#ccc", backgroundColor: "#111",
            padding: "6px 8px", borderRadius: "3px", marginTop: "4px",
            overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all",
            maxHeight: "300px", overflowY: "auto",
          }}>
            <span style={{ color: "#888", display: "block", marginBottom: "2px", fontSize: "10px" }}>INPUT</span>
            {JSON.stringify(entry.input, null, 2)}
          </pre>
        )}
        {entry.result && (
          <pre style={{
            fontSize: "11px", color: "#ccc", backgroundColor: "#111",
            padding: "6px 8px", borderRadius: "3px", marginTop: "4px",
            overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all",
            maxHeight: "300px", overflowY: "auto",
          }}>
            <span style={{ color: "#26a69a", display: "block", marginBottom: "2px", fontSize: "10px" }}>RESPONSE</span>
            {JSON.stringify(entry.result, null, 2)}
          </pre>
        )}
        {!entry.input && !entry.result && (entry.text || entry.tools_called || entry.error || entry) && (
          <pre style={{
            fontSize: "11px", color: "#ccc", backgroundColor: "#6d4646",
            padding: "6px 8px", borderRadius: "3px", marginTop: "4px",
            overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all",
            maxHeight: "300px", overflowY: "auto",
          }}>
            {JSON.stringify(entry.text || entry.tools_called || entry.error || entry, null, 2)}
          </pre>
        )}
      </Collapse>
    </div>
  );
}

/**
 * Agent tab for PlayEngine bases.
 * Streams an agent run over the WebSocket server (/socket/ws) with the current
 * versionId, surfacing turns live in the admin console.
 *
 * @param props.selectedVersion          {Object}
 * @param props.selectedBase             {Object}
 * @param props.versions                 {Array}
 * @param props.createSnackbar           {function}
 * @param props.isAdmin                  {boolean}
 */
function PlayEngineAgent(props) {
  const [prompt, setPrompt] = useState("");
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [consoleOpen, setConsoleOpen] = useState(false);
  // Events streamed live from the agent socket during a run (realtime console).
  const [liveLog, setLiveLog] = useState([]);
  // Another client/tab has a run in progress on this version.
  const [remoteRunning, setRemoteRunning] = useState(false);
  // Single-level undo of the last agent run.
  const [canUndo, setCanUndo] = useState(false);
  const [undoing, setUndoing] = useState(false);
  // Checkpoint history drawer.
  const [checkpoints, setCheckpoints] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  // Checkpoint pending restore confirmation (null when the dialog is closed).
  const [confirmCheckpoint, setConfirmCheckpoint] = useState(null);
  // Paused segment awaiting the user's continue/stop decision (null = no dialog).
  const [continueInfo, setContinueInfo] = useState(null);
  // Version the in-flight handleRun() websocket belongs to (survives tab switches).
  const activeRunVersionIdRef = useRef(null);
  const selectedVersionIdRef = useRef(null);

  const versionId = props.selectedVersion && props.selectedVersion._id;
  const isCreationMode = isVersionEmpty(props.selectedVersion) && !versionHasPreset(props.selectedVersion);

  useEffect(() => {
    selectedVersionIdRef.current = versionId || null;
  }, [versionId]);

  useEffect(() => {
    setPrompt("");
    setResponse(null);
    setLoading(false);
    setConsoleOpen(false);
    setLiveLog([]);
    setRemoteRunning(false);
    setCanUndo(false);
    setUndoing(false);
    setCheckpoints([]);
    setHistoryOpen(false);
    setRestoringId(null);
    setConfirmCheckpoint(null);
    setContinueInfo(null);
  }, [versionId]);

  function isViewingVersion(targetVersionId) {
    if (!targetVersionId) return false;
    return String(targetVersionId) === String(selectedVersionIdRef.current);
  }

  // Push agent-run version state into the selected version and reload the preview iframe.
  // Only applies when targetVersionId matches what the user is currently viewing.
  // dashboardMeta must be applied alongside playEngineConfig so debounced saves do not
  // overwrite agent-written fields (e.g. dashboardMeta.gameFlow) with stale client state.
  function applyAgentVersionState({ playEngineConfig, dashboardMeta }, targetVersionId) {
    if (!props.setSelectedVersionPathValue) return;
    if (!isViewingVersion(targetVersionId)) return;
    if (playEngineConfig != null) {
      props.setSelectedVersionPathValue({ path: "playEngineConfig", value: playEngineConfig });
    }
    if (dashboardMeta != null) {
      props.setSelectedVersionPathValue({ path: "dashboardMeta", value: dashboardMeta });
    }
    if (playEngineConfig != null) {
      window.dispatchEvent(new CustomEvent("playengine:agent-reload"));
    }
  }

  function applyConfig(config, targetVersionId, dashboardMeta = null) {
    applyAgentVersionState({ playEngineConfig: config, dashboardMeta }, targetVersionId);
  }

  function findVersionTitle(targetVersionId) {
    if (!targetVersionId || !props.versions) return null;
    const match = props.versions.find((v) => String(v._id) === String(targetVersionId));
    return match && match.title;
  }

  function markRunConsumed(runVersionId, agentRunId) {
    if (runVersionId && agentRunId) {
      consumedAgentRunByVersion.set(runVersionId, agentRunId);
    }
  }

  function isRunAlreadyConsumed(runVersionId, agentRunId) {
    return Boolean(agentRunId && consumedAgentRunByVersion.get(runVersionId) === agentRunId);
  }

  async function consumeRunOnServer(runVersionId) {
    if (!runVersionId) return;
    await acknowledgeRun(runVersionId);
  }

  function applySuccessResult(result, { away = false, runVersionId: runVersionIdOpt } = {}) {
    const runVersionId = runVersionIdOpt || activeRunVersionIdRef.current || versionId;
    const isViewingRunVersion = isViewingVersion(runVersionId);
    const agentRunId = result.agentRunId;

    markRunConsumed(runVersionId, agentRunId);
    void consumeRunOnServer(runVersionId);

    if (isViewingRunVersion) {
      setResponse({
        summary: result.summary,
        changes: result.changes || [],
        creditCost: result.creditCost != null ? result.creditCost : null,
        balanceAfter: result.balanceAfter != null ? result.balanceAfter : null,
        debugLog: result.debugLog || [],
        tokenUsage: result.tokenUsage || null,
        providerName: result.providerName || null,
        model: result.model || null,
        usdCost: result.usdCost != null ? result.usdCost : null,
        pricingExplanation: result.pricingExplanation || null,
      });
      applyConfig(result.updatedPlayEngineConfig, runVersionId, result.updatedDashboardMeta);
      if (props.refreshCompany) void props.refreshCompany();
      setCanUndo(Boolean(agentRunId));
      void fetchCheckpoints(runVersionId);
      if (result.paused) {
        // Segment paused at its turn limit — ask the user whether to continue.
        setContinueInfo({
          summary: result.summary,
          cumulativeTurns: result.cumulativeTurns,
          canContinue: result.canContinue !== false,
        });
      }
      if (away && props.createSnackbar) {
        props.createSnackbar(
          result.paused
            ? "Agent paused at its turn limit while you were away"
            : "Agent run completed while you were away",
          result.paused ? "info" : "success"
        );
      }
      return;
    }

    pendingAgentResultsByVersion.set(runVersionId, {
      type: "success",
      result,
      away: true,
    });
    const title = findVersionTitle(runVersionId);
    if (props.createSnackbar) {
      props.createSnackbar(
        title
          ? `Agent run completed on "${title}"`
          : "Agent run completed on another version",
        "success"
      );
    }
    window.dispatchEvent(new CustomEvent("playengine:agent-versions-refresh"));
  }

  function applyFailureStatus(status, { away = false, runVersionId: runVersionIdOpt } = {}) {
    const runVersionId = runVersionIdOpt || activeRunVersionIdRef.current || versionId;
    const isViewingRunVersion = isViewingVersion(runVersionId);
    const agentRunId = status.agentRunId;

    markRunConsumed(runVersionId, agentRunId);
    void consumeRunOnServer(runVersionId);

    if (isViewingRunVersion) {
      if (status.restoredPlayEngineConfig != null) {
        applyConfig(status.restoredPlayEngineConfig, runVersionId, status.restoredDashboardMeta);
      }
      setResponse({
        error: status.error || "Agent run failed",
        restored: Boolean(status.restored),
        creditCost: status.creditCost != null ? status.creditCost : null,
        balanceAfter: status.balanceAfter != null ? status.balanceAfter : null,
        pricingExplanation: status.pricingExplanation || null,
      });
      if (props.refreshCompany) void props.refreshCompany();
      if (away && props.createSnackbar) {
        props.createSnackbar("Agent run failed while you were away", "warning");
      }
      return;
    }

    pendingAgentResultsByVersion.set(runVersionId, {
      type: "failed",
      status,
      away: true,
    });
    const title = findVersionTitle(runVersionId);
    if (props.createSnackbar) {
      props.createSnackbar(
        title
          ? `Agent run failed on "${title}"`
          : "Agent run failed on another version",
        "warning"
      );
    }
    window.dispatchEvent(new CustomEvent("playengine:agent-versions-refresh"));
  }

  function applyPendingResultForVersion(targetVersionId) {
    const pending = pendingAgentResultsByVersion.get(targetVersionId);
    if (!pending) return;
    pendingAgentResultsByVersion.delete(targetVersionId);
    if (pending.type === "success") {
      applySuccessResult(pending.result, { away: pending.away, runVersionId: targetVersionId });
    } else if (pending.type === "failed") {
      applyFailureStatus(pending.status, { away: pending.away, runVersionId: targetVersionId });
    }
  }

  // On version switch / page load, recover from an in-flight or recently finished run.
  useEffect(() => {
    if (!versionId) return undefined;

    let cancelled = false;
    let pollTimer = null;

    async function handleSettledStatus(status, { away = true } = {}) {
      const agentRunId = status.agentRunId || (status.result && status.result.agentRunId);
      if (agentRunId && isRunAlreadyConsumed(versionId, agentRunId)) {
        await consumeRunOnServer(versionId);
        applyPendingResultForVersion(versionId);
        return;
      }

      if (status.status === "completed" && status.result) {
        applySuccessResult(status.result, { away, runVersionId: versionId });
      } else if (status.status === "failed") {
        applyFailureStatus(status, { away, runVersionId: versionId });
      } else {
        applyPendingResultForVersion(versionId);
      }
    }

    async function checkRunStatus() {
      try {
        const status = await fetchRunStatus(versionId);
        if (cancelled) return;

        if (status.status === "running") {
          setRemoteRunning(true);
          setLoading(true);
          pollTimer = setInterval(async () => {
            try {
              const next = await fetchRunStatus(versionId);
              if (cancelled) return;
              if (next.status !== "running") {
                clearInterval(pollTimer);
                pollTimer = null;
                setRemoteRunning(false);
                setLoading(false);
                await handleSettledStatus(next);
              }
            } catch (_err) {
              // Keep polling — transient network blip.
            }
          }, 3000);
        } else if (status.status === "completed" || status.status === "failed") {
          await handleSettledStatus(status);
        } else {
          applyPendingResultForVersion(versionId);
        }
      } catch (_err) {
        // Status check is best-effort on load.
      }
    }

    checkRunStatus();

    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [versionId]);

  // Load the checkpoint history for a version. Only updates state if the user is
  // still viewing that version when the response arrives.
  async function fetchCheckpoints(targetVersionId = versionId) {
    if (!targetVersionId) return;
    try {
      const res = await fetch(getServerUri("agent/list-checkpoints"), {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          accessToken: localStorage.getItem("session.accessToken"),
          versionId: targetVersionId,
        }),
      });
      if (!res.ok) return;
      const data = await res.json().catch(() => ({}));
      if (data.ok && Array.isArray(data.checkpoints) && isViewingVersion(targetVersionId)) {
        setCheckpoints(data.checkpoints);
      }
    } catch (err) {
      // Best-effort — the drawer just won't populate.
    }
  }

  // Load the checkpoint history when the selected version changes.
  useEffect(() => {
    if (!versionId) return;
    fetchCheckpoints(versionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionId]);

  // Restore a specific checkpoint from the history drawer. The server snapshots
  // the current state first, so this action is itself undoable.
  async function handleRestoreCheckpoint(checkpointId) {
    if (!versionId || !checkpointId || restoringId || loading || remoteRunning) return;
    setRestoringId(checkpointId);
    try {
      const res = await fetch(getServerUri("agent/restore-checkpoint"), {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          accessToken: localStorage.getItem("session.accessToken"),
          versionId,
          checkpointId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok && data.restoredPlayEngineConfig != null) {
        applyConfig(data.restoredPlayEngineConfig, versionId, data.restoredDashboardMeta);
        setCanUndo(false);
        if (props.createSnackbar) props.createSnackbar("Restored checkpoint", "success");
        await fetchCheckpoints(versionId);
      } else {
        if (props.createSnackbar) props.createSnackbar(data.error || "Could not restore checkpoint", "warning");
        await fetchCheckpoints(versionId);
      }
    } catch (err) {
      if (props.createSnackbar) props.createSnackbar(err.message || "Restore failed", "error");
    } finally {
      setRestoringId(null);
    }
  }

  // Confirm the pending restore from the dialog, then run it.
  async function confirmRestore() {
    const target = confirmCheckpoint;
    setConfirmCheckpoint(null);
    if (target && target.checkpointId) {
      await handleRestoreCheckpoint(target.checkpointId);
    }
  }

  // Ask the server to roll back to the last checkpoint. Used as the fallback
  // when a run times out or fails without returning a restored config.
  async function restoreCheckpointFromServer(targetVersionId = versionId) {
    if (!targetVersionId) return false;
    try {
      const res = await fetch(getServerUri("agent/restore-checkpoint"), {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          accessToken: localStorage.getItem("session.accessToken"),
          versionId: targetVersionId,
        }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      if (data.ok && data.restoredPlayEngineConfig != null) {
        applyConfig(data.restoredPlayEngineConfig, targetVersionId, data.restoredDashboardMeta);
        return true;
      }
    } catch (err) {
      // Swallow — caller surfaces the original failure.
    }
    return false;
  }

  async function handleRun(opts = {}) {
    // Called both from the run button (click event as opts) and the continue
    // dialog ({ continueRun: true }).
    const isContinue = opts && opts.continueRun === true;
    if (!isContinue && !prompt.trim()) return;
    if (!versionId || remoteRunning) return;
    if (!hasAgentCreditsRemaining(props.userCompany)) {
      notifyAgentCreditsExhausted(props.createSnackbar);
      return;
    }
    const runVersionId = versionId;
    setLoading(true);
    setResponse(null);
    setLiveLog([]);
    setCanUndo(false);
    setRemoteRunning(false);
    setContinueInfo(null);
    activeRunVersionIdRef.current = runVersionId;

    const mode = isCreationMode ? "create" : "edit";

    try {
      const result = await runAgentOverSocket({
        mode,
        versionId: runVersionId,
        prompt: isContinue ? "" : prompt.trim(),
        continueRun: isContinue,
        // Stream debug events to the admin console only (server omits them for others).
        onEvent: props.isAdmin
          ? (event) => {
            if (isViewingVersion(runVersionId)) {
              setLiveLog((prev) => [...prev, event]);
            }
          }
          : undefined,
      });

      applySuccessResult(result, { runVersionId });
    } catch (err) {
      if (handleAgentCreditsExhaustedError(err, props.createSnackbar)) return;
      // Another tab/session already started a run — poll for its outcome instead
      // of restoring the checkpoint (which would undo an in-flight run).
      if (err.code === "ALREADY_RUNNING") {
        if (isViewingVersion(runVersionId)) {
          setRemoteRunning(true);
          setResponse({ error: err.message || "An agent run is already in progress for this version" });
        }
        try {
          const status = await pollRunUntilSettled(runVersionId);
          if (status.status === "completed" && status.result) {
            applySuccessResult(status.result, { runVersionId });
          } else if (status.status === "failed") {
            applyFailureStatus(status, { runVersionId });
          }
        } catch (pollErr) {
          if (isViewingVersion(runVersionId)) {
            setResponse({ error: pollErr.message || err.message });
          }
        }
        return;
      }

      // Connection dropped mid-run — the server keeps going; poll for the outcome
      // instead of immediately restoring the checkpoint (which would race).
      if (err.isDisconnect) {
        if (isViewingVersion(runVersionId)) {
          setResponse({ error: "Connection lost — waiting for agent run to finish…" });
        }
        try {
          const status = await pollRunUntilSettled(runVersionId);
          if (status.status === "completed" && status.result) {
            applySuccessResult(status.result, { runVersionId });
          } else if (status.status === "failed") {
            applyFailureStatus(status, { runVersionId });
          } else if (isViewingVersion(runVersionId)) {
            setResponse({ error: "Connection lost and run status could not be determined" });
          }
        } catch (pollErr) {
          if (isViewingVersion(runVersionId)) {
            setResponse({ error: pollErr.message || "Connection lost while agent was running" });
          }
        }
        return;
      }

      // On a handled agent failure the server already rolled back and sent the
      // restored config; otherwise fall back to restoring the checkpoint.
      let restored = false;
      if (err.restoredPlayEngineConfig != null) {
        applyConfig(err.restoredPlayEngineConfig, runVersionId, err.restoredDashboardMeta);
        restored = true;
      } else if (err.restored) {
        restored = true;
      } else {
        restored = await restoreCheckpointFromServer(runVersionId);
      }
      if (isViewingVersion(runVersionId)) {
        setResponse({
          error: err.message || "Agent run failed",
          restored,
          creditCost: err.creditCost != null ? err.creditCost : null,
          balanceAfter: err.balanceAfter != null ? err.balanceAfter : null,
          pricingExplanation: err.pricingExplanation || null,
        });
        if (props.refreshCompany) void props.refreshCompany();
      }
    } finally {
      if (activeRunVersionIdRef.current === runVersionId) {
        activeRunVersionIdRef.current = null;
      }
      setLoading(false);
      setRemoteRunning(false);
    }
  }

  async function handleUndo() {
    if (!versionId || undoing) return;
    setUndoing(true);
    try {
      const res = await fetch(getServerUri("agent/restore-checkpoint"), {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          accessToken: localStorage.getItem("session.accessToken"),
          versionId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok && data.restoredPlayEngineConfig != null) {
        applyConfig(data.restoredPlayEngineConfig, versionId, data.restoredDashboardMeta);
        setCanUndo(false);
        void fetchCheckpoints(versionId);
        if (props.createSnackbar) props.createSnackbar("Reverted to the previous version", "success");
      } else {
        // Checkpoint already expired (>24h) or overwritten.
        setCanUndo(false);
        void fetchCheckpoints(versionId);
        if (props.createSnackbar) props.createSnackbar(data.error || "Nothing to undo", "warning");
      }
    } catch (err) {
      if (props.createSnackbar) props.createSnackbar(err.message || "Undo failed", "error");
    } finally {
      setUndoing(false);
    }
  }

  function handleContinueRun() {
    setContinueInfo(null);
    void handleRun({ continueRun: true });
  }

  function handleStopKeep() {
    // Work done so far is already applied and undoable — nothing else to do.
    setContinueInfo(null);
  }

  async function handleStopUndo() {
    setContinueInfo(null);
    await handleUndo();
  }

  // Prefer the live stream (populated during a run) so the console updates in
  // realtime; fall back to the final debugLog from the completed response.
  const debugLog = (liveLog && liveLog.length > 0) ? liveLog : (response && response.debugLog);
  const tokenUsage = response && response.tokenUsage;
  const providerName = response && response.providerName;
  const agentModel = response && response.model;
  const usdCost = response && response.usdCost;
  const pricingExplanation = response && response.pricingExplanation;

  return (
    <div style={{ padding: "12px", display: "flex", flexDirection: "column", gap: "12px", height: "100%", boxSizing: "border-box" }}>
      {!versionId && (
        <Typography variant="body2" style={{ color: "#f44336", fontSize: "12px" }}>
          No version selected.
        </Typography>
      )}

      {versionId && (loading || remoteRunning) && (
        <div style={{ backgroundColor: "#1a2233", border: "1px solid #1565c0", borderRadius: "6px", padding: "8px 12px" }}>
          <Typography variant="caption" style={{ color: "#90caf9", fontWeight: 700, display: "block", marginBottom: "2px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Agent Running
          </Typography>
          <Typography variant="caption" style={{ color: "#bbdefb", lineHeight: "1.5" }}>
            {remoteRunning
              ? "Another session is running the agent on this version. Waiting for it to finish…"
              : "Agent run in progress. You can leave this tab — the server will keep running and this page will update when it finishes."}
          </Typography>
        </div>
      )}

      {versionId && isCreationMode && (
        <div style={{ backgroundColor: "#1a2a1a", border: "1px solid #2e7d32", borderRadius: "6px", padding: "8px 12px" }}>
          <Typography variant="caption" style={{ color: "#81c784", fontWeight: 700, display: "block", marginBottom: "2px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Create from Scratch
          </Typography>
          <Typography variant="caption" style={{ color: "#a5d6a7", lineHeight: "1.5" }}>
            This version is empty and has no preset. Describe the playable you want to build and the agent will create it from scratch.
          </Typography>
        </div>
      )}

      <Card style={{ backgroundColor: CARD_BG }}>
        <CardContent>
          <AgentPromptHeader userCompany={props.userCompany} />

          <TextField
            multiline
            minRows={5}
            maxRows={12}
            fullWidth
            variant="outlined"
            placeholder={isCreationMode
              ? "Describe the playable you want to create from scratch (e.g. 'A match-3 game with 3 levels, power-ups, and a timer')..."
              : "Describe what you want the agent to do with this playEngine version..."
            }
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            InputProps={{
              style: {
                color: "#fff",
                backgroundColor: FIELD_BG,
                fontSize: "13px",
              },
            }}
            inputProps={{ style: { lineHeight: "1.6" } }}
          />

          <Button
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading || remoteRunning || !prompt.trim() || !versionId}
            onClick={handleRun}
            style={{ marginTop: "10px", height: "36px" }}
          >
            {loading ? <CircularProgress size={18} style={{ color: "#fff" }} /> : (isCreationMode ? "Create Playable" : "Run Agent")}
          </Button>
        </CardContent>
      </Card>

      {response && (
        <Card style={{ backgroundColor: CARD_BG }}>
          <CardContent>
            <Typography
              variant="caption"
              style={{ color: "#aaa", display: "block", marginBottom: "8px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}
            >
              {response.error ? "Error" : "Summary"}
            </Typography>
            <Typography
              variant="body2"
              style={{
                color: response.error ? "#f44336" : "#e0e0e0",
                whiteSpace: "pre-wrap",
                lineHeight: "1.7",
                fontSize: "13px",
                backgroundColor: FIELD_BG,
                padding: "10px 12px",
                borderRadius: "4px",
              }}
            >
              {response.error ?? response.summary}
            </Typography>

            {response.creditCost != null && (
              <Typography variant="caption" style={{ color: "#80cbc4", display: "block", marginTop: "6px", fontSize: "12px", fontFamily: "monospace" }}>
                This run: {response.creditCost.toLocaleString()} AI credits
                {response.balanceAfter != null && ` · ${Math.max(0, response.balanceAfter).toLocaleString()} left`}
              </Typography>
            )}

            {response.error && response.restored && (
              <Typography variant="caption" style={{ color: "#ffb74d", display: "block", marginTop: "6px", fontSize: "12px" }}>
                Restored to the previous version.
              </Typography>
            )}

            {!response.error && canUndo && (
              <Button
                variant="outlined"
                size="small"
                startIcon={undoing ? <CircularProgress size={14} style={{ color: "#fff" }} /> : <UndoIcon style={{ fontSize: 16 }} />}
                disabled={undoing}
                onClick={handleUndo}
                style={{ marginTop: "10px", color: "#fff", borderColor: "#777" }}
              >
                {undoing ? "Undoing…" : "Undo last change"}
              </Button>
            )}

            {response.changes && response.changes.length > 0 && (
              <div style={{ marginTop: "10px" }}>
                <Typography
                  variant="caption"
                  style={{ color: "#aaa", display: "block", marginBottom: "6px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}
                >
                  Changes made ({response.changes.length})
                </Typography>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  {response.changes.map((change, i) => (
                    <div
                      key={i}
                      style={{
                        backgroundColor: FIELD_BG,
                        borderRadius: "4px",
                        padding: "6px 10px",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "8px",
                      }}
                    >
                      <CheckCircleOutlineIcon style={{ color: "#4caf50", fontSize: "16px", marginTop: "1px", flexShrink: 0 }} />
                      <Typography variant="body2" style={{ color: "#ccc", fontSize: "12px", lineHeight: "1.5" }}>
                        <span style={{ color: "#90caf9", fontWeight: 600 }}>{change.tool}</span>
                        {change.input && change.input.componentId && ` — ${change.input.componentId}`}
                        {change.input && change.input.dependencyKey && ` › ${change.input.dependencyKey}`}
                        {change.input && change.input.path && ` › ${change.input.path}`}
                      </Typography>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Bottom-pinned panels (version history + admin console) ─────── */}
      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column" }}>

        {/* ── Version History drawer (expands upward) ────────────────────── */}
        {versionId && (
          <div>
            <Collapse in={historyOpen}>
              <div
                style={{
                  backgroundColor: CONSOLE_BG,
                  padding: "8px 10px",
                  maxHeight: "260px",
                  overflowY: "auto",
                  borderTop: "1px solid #333",
                }}
              >
                {checkpoints.length === 0 ? (
                  <Typography variant="caption" style={{ color: "#555", fontFamily: "monospace" }}>
                    No checkpoints yet.
                  </Typography>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    {checkpoints.map((cp) => {
                      const isRestorePoint = cp.source === "restore";
                      const label = isRestorePoint
                        ? "Restore point (pre-restore snapshot)"
                        : (cp.prompt || "Agent run");
                      const isThisRestoring = restoringId === cp.checkpointId;
                      return (
                        <div
                          key={cp.checkpointId}
                          style={{
                            backgroundColor: FIELD_BG,
                            borderRadius: "4px",
                            padding: "6px 10px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <div style={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              style={{
                                color: isRestorePoint ? "#90a4ae" : "#ccc",
                                fontSize: "12px",
                                lineHeight: "1.4",
                                fontStyle: isRestorePoint ? "italic" : "normal",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={label}
                            >
                              {label}
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{ color: "#777", fontSize: "11px" }}
                              title={formatExactDateTime(cp.dateCreated)}
                            >
                              {formatExactDateTime(cp.dateCreated)}
                              {timeAgo(cp.dateCreated) && (
                                <span style={{ color: "#555", marginLeft: "6px" }}>
                                  ({timeAgo(cp.dateCreated)})
                                </span>
                              )}
                            </Typography>
                          </div>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={isThisRestoring
                              ? <CircularProgress size={12} style={{ color: "#fff" }} />
                              : <RestoreIcon style={{ fontSize: 15 }} />}
                            disabled={Boolean(restoringId) || loading || remoteRunning}
                            onClick={() => setConfirmCheckpoint(cp)}
                            style={{ color: "#fff", borderColor: "#777", flexShrink: 0, minWidth: "92px" }}
                          >
                            {isThisRestoring ? "Restoring…" : "Restore"}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </Collapse>

            <Divider style={{ backgroundColor: "#555", marginBottom: "0" }} />
            <div
              onClick={() => setHistoryOpen((v) => !v)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 10px",
                backgroundColor: CONSOLE_BG,
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <HistoryIcon style={{ fontSize: 15, color: "#4db6ac" }} />
              <Typography variant="caption" style={{ color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "11px", flexGrow: 1 }}>
                Version History
                {checkpoints.length > 0 && (
                  <span style={{ marginLeft: "8px", color: "#4db6ac" }}>{checkpoints.length}</span>
                )}
              </Typography>
              {historyOpen ? (
                <ExpandMoreIcon style={{ fontSize: 16, color: "#888" }} />
              ) : (
                <ExpandLessIcon style={{ fontSize: 16, color: "#888" }} />
              )}
            </div>
          </div>
        )}

        {/* ── Admin Console ──────────────────────────────────────────────── */}
        {props.isAdmin && (
          <div>
            <Divider style={{ backgroundColor: "#555", marginBottom: "0" }} />

            {/* Console header / toggle bar */}
            <div
              onClick={() => setConsoleOpen((v) => !v)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 10px",
                backgroundColor: CONSOLE_BG,
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <BugReportIcon style={{ fontSize: 15, color: "#7e57c2" }} />
              <Typography variant="caption" style={{ color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "11px", flexGrow: 1 }}>
                Admin Console
                {debugLog && debugLog.length > 0 && (
                  <span style={{ marginLeft: "8px", color: "#7e57c2" }}>{debugLog.length} events</span>
                )}
                {providerName && agentModel && (
                  <span style={{ marginLeft: "8px", color: "#4db6ac", fontFamily: "monospace", textTransform: "none", fontWeight: 400 }}>
                    {providerName} / {agentModel}
                  </span>
                )}
                {tokenUsage && (
                  <span style={{ marginLeft: "8px", color: "#546e7a", fontFamily: "monospace" }}>
                    ↑{tokenUsage.input.toLocaleString()} ↓{tokenUsage.output.toLocaleString()} tokens
                    {usdCost != null && usdCost > 0 && (
                      <span style={{ marginLeft: "6px", color: "#78909c" }}>(${usdCost.toFixed(6)})</span>
                    )}
                  </span>
                )}
              </Typography>
              {consoleOpen ? (
                <ExpandLessIcon style={{ fontSize: 16, color: "#888" }} />
              ) : (
                <ExpandMoreIcon style={{ fontSize: 16, color: "#888" }} />
              )}
            </div>

            <Collapse in={consoleOpen}>
              <div
                style={{
                  backgroundColor: CONSOLE_BG,
                  padding: "8px 10px",
                  maxHeight: "340px",
                  overflowY: "auto",
                  borderTop: "1px solid #333",
                }}
              >
                {pricingExplanation && (
                  <Typography
                    variant="caption"
                    style={{
                      color: "#a5d6a7",
                      display: "block",
                      marginBottom: "10px",
                      fontSize: "11px",
                      lineHeight: "1.6",
                      fontFamily: "monospace",
                      backgroundColor: "#1e2a1e",
                      border: "1px solid #2e5a30",
                      borderRadius: "4px",
                      padding: "8px 10px",
                    }}
                  >
                    {pricingExplanation}
                  </Typography>
                )}
                {!debugLog || debugLog.length === 0 ? (
                  <Typography variant="caption" style={{ color: "#555", fontFamily: "monospace" }}>
                    {loading ? "Agent running…" : "No log yet — run the agent to see workflow events."}
                  </Typography>
                ) : (
                  debugLog.map((entry, i) => <LogEntry key={i} entry={entry} />)
                )}
              </div>
            </Collapse>
          </div>
        )}
      </div>

      {/* ── Restore confirmation dialog ────────────────────────────────── */}
      <Dialog
        open={Boolean(confirmCheckpoint)}
        onClose={() => setConfirmCheckpoint(null)}
        PaperProps={{ style: { backgroundColor: CARD_BG, color: "#fff" } }}
      >
        <DialogTitle style={{ color: "#fff" }}>Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ color: "#ccc" }}>
            This will replace the current version with the selected checkpoint. Your current
            state is snapshotted first, so you can undo this restore.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCheckpoint(null)} style={{ color: "#ccc" }}>
            Cancel
          </Button>
          <Button onClick={confirmRestore} variant="contained" color="primary" autoFocus>
            Restore
          </Button>
        </DialogActions>
      </Dialog>

      <AgentContinueDialog
        open={Boolean(continueInfo)}
        info={continueInfo}
        onContinue={handleContinueRun}
        onKeep={handleStopKeep}
        onUndo={handleStopUndo}
        undoDisabled={undoing}
      />
    </div>
  );
}

export default PlayEngineAgent;

